/** How the storage map editor chose its pixel grid (1 px ≈ 1 occupancy cell when aligned). */
export type MapEditorGridSource =
    | "aurora_2d_export"
    | "ros_metadata"
    | "live_snapshot"
    | "companion_preview"
    | "preview_image";

export type MapEditorGridDims = {
    width: number;
    height: number;
    source: MapEditorGridSource;
};

export type MapMetadataLike = {
    width?: number;
    height?: number;
    resolution?: number;
    origin?: { x?: number; y?: number };
    originYaw?: number;
} | null | undefined;

/** Live occupancy grid captured on Control when opening the editor (survives provider remount). */
export type MapEditorLiveGridSnapshot = {
    width: number;
    height: number;
    resolution: number;
    origin?: { x: number; y: number };
    originYaw?: number;
};

export type NaturalImageDims = { w: number; h: number } | null;

function finitePositive(n: unknown): n is number {
    return typeof n === "number" && Number.isFinite(n) && n > 0;
}

function isCompleteGridMetadata(meta: MapMetadataLike): meta is {
    width: number;
    height: number;
    resolution: number;
    origin?: { x?: number; y?: number };
    originYaw?: number;
} {
    return (
        finitePositive(meta?.width) &&
        finitePositive(meta?.height) &&
        finitePositive(meta?.resolution)
    );
}

/** Build a portable snapshot from MapProvider `mapMetadata` for navigation state. */
export function liveGridSnapshotFromMapMetadata(
    meta: MapMetadataLike,
): MapEditorLiveGridSnapshot | null {
    if (!isCompleteGridMetadata(meta)) {
        return null;
    }
    const snap: MapEditorLiveGridSnapshot = {
        width: Math.round(meta.width),
        height: Math.round(meta.height),
        resolution: meta.resolution,
    };
    const ox = meta.origin?.x;
    const oy = meta.origin?.y;
    if (typeof ox === "number" && Number.isFinite(ox) && typeof oy === "number" && Number.isFinite(oy)) {
        snap.origin = { x: ox, y: oy };
    }
    if (typeof meta.originYaw === "number" && Number.isFinite(meta.originYaw)) {
        snap.originYaw = meta.originYaw;
    }
    return snap;
}

/** Parse snapshot from router state / sessionStorage. */
export function parseMapEditorLiveGridSnapshot(raw: unknown): MapEditorLiveGridSnapshot | null {
    if (!raw || typeof raw !== "object") {
        return null;
    }
    const o = raw as MapEditorLiveGridSnapshot;
    if (!finitePositive(o.width) || !finitePositive(o.height) || !finitePositive(o.resolution)) {
        return null;
    }
    const snap: MapEditorLiveGridSnapshot = {
        width: Math.round(o.width),
        height: Math.round(o.height),
        resolution: o.resolution,
    };
    const ox = (o.origin as { x?: unknown } | undefined)?.x;
    const oy = (o.origin as { y?: unknown } | undefined)?.y;
    if (typeof ox === "number" && Number.isFinite(ox) && typeof oy === "number" && Number.isFinite(oy)) {
        snap.origin = { x: ox, y: oy };
    }
    if (typeof o.originYaw === "number" && Number.isFinite(o.originYaw)) {
        snap.originYaw = o.originYaw;
    }
    return snap;
}

/** Prefer live `/map_metadata` from MapProvider; else snapshot from Control navigation. */
export function pickMapEditorGridMetadata(
    liveMeta: MapMetadataLike,
    liveSnapshot: MapEditorLiveGridSnapshot | null | undefined,
): MapMetadataLike {
    if (isCompleteGridMetadata(liveMeta)) {
        return liveMeta;
    }
    if (!liveSnapshot) {
        return liveMeta;
    }
    return {
        width: liveSnapshot.width,
        height: liveSnapshot.height,
        resolution: liveSnapshot.resolution,
        ...(liveSnapshot.origin ? { origin: liveSnapshot.origin } : {}),
        ...(typeof liveSnapshot.originYaw === "number"
            ? { originYaw: liveSnapshot.originYaw }
            : {}),
    };
}

/** True when width, height, and resolution are all valid for live-grid sizing. */
export function isCompleteMapEditorGridMetadata(meta: MapMetadataLike): boolean {
    return isCompleteGridMetadata(meta);
}

/** True when 1 editor pixel ≈ 1 map cell for the chosen grid (safe to save filter mask). */
export function isMapEditorGridCellAligned(source: MapEditorGridSource | undefined): boolean {
    return (
        source === "aurora_2d_export" ||
        source === "ros_metadata" ||
        source === "live_snapshot" ||
        source === "companion_preview" ||
        source === "preview_image"
    );
}

/** Editor canvas matches Aurora /preview-bmp pixels (not live /map_metadata dimensions). */
export function isAurora2dExportGridSource(source: MapEditorGridSource | undefined): boolean {
    return source === "aurora_2d_export";
}

/**
 * When true, stretch the storage preview to the live /map_metadata canvas (ros grid ≠ export).
 * When false, canvas size matches Aurora 2D export (1:1 with preview-bmp).
 */
export function editorUsesGridFillLayout(source: MapEditorGridSource | undefined): boolean {
    return source === "ros_metadata" || source === "live_snapshot";
}

/** Live grid size from /map_metadata when available (for debug / hints only). */
export function liveOccupancyGridDims(
    liveMeta: MapMetadataLike,
    liveSnapshot?: MapEditorLiveGridSnapshot | null,
): { width: number; height: number } | null {
    if (isCompleteGridMetadata(liveMeta)) {
        return {
            width: Math.round(liveMeta.width),
            height: Math.round(liveMeta.height),
        };
    }
    if (liveSnapshot && finitePositive(liveSnapshot.width) && finitePositive(liveSnapshot.height)) {
        return {
            width: Math.round(liveSnapshot.width),
            height: Math.round(liveSnapshot.height),
        };
    }
    return null;
}

/**
 * Pick editor canvas size from Aurora 2D export (preview-bmp) when available.
 * Filter YAML still uses live origin/resolution via {@link pickMapEditorGridMetadata}.
 * Falls back to /map_metadata only when preview dimensions are unknown.
 */
export function resolveMapEditorGridDims(
    liveMeta: MapMetadataLike,
    previewNatural: NaturalImageDims,
    companionNatural: NaturalImageDims,
    liveSnapshot?: MapEditorLiveGridSnapshot | null,
): MapEditorGridDims | null {
    const layout = pickMapEditorLayoutNatural(previewNatural, companionNatural);
    if (layout && layout.w > 0 && layout.h > 0) {
        return {
            width: layout.w,
            height: layout.h,
            source: "aurora_2d_export",
        };
    }
    if (previewNatural && previewNatural.w > 0 && previewNatural.h > 0) {
        return {
            width: previewNatural.w,
            height: previewNatural.h,
            source: "preview_image",
        };
    }
    if (companionNatural && companionNatural.w > 0 && companionNatural.h > 0) {
        return {
            width: companionNatural.w,
            height: companionNatural.h,
            source: "companion_preview",
        };
    }
    if (isCompleteGridMetadata(liveMeta)) {
        return {
            width: Math.round(liveMeta.width),
            height: Math.round(liveMeta.height),
            source: "ros_metadata",
        };
    }
    if (liveSnapshot && finitePositive(liveSnapshot.width) && finitePositive(liveSnapshot.height)) {
        return {
            width: Math.round(liveSnapshot.width),
            height: Math.round(liveSnapshot.height),
            source: "live_snapshot",
        };
    }
    return null;
}

/** True when preview pixels are already 1:1 with the occupancy grid. */
export function previewMatchesGridPixelSize(
    previewW: number,
    previewH: number,
    gridW: number,
    gridH: number,
): boolean {
    return previewW === gridW && previewH === gridH;
}

/**
 * Source rect for stretching a raster into the layout frame.
 * When variant pixels match the companion/layout size, use layout dims so bg + overlay share one grid.
 */
export function rasterSourceDimsAlignedToLayout(
    rasterNatural: NaturalImageDims,
    layoutNatural: NaturalImageDims,
): { w: number; h: number } {
    if (!rasterNatural) {
        return { w: layoutNatural?.w ?? 1, h: layoutNatural?.h ?? 1 };
    }
    if (
        layoutNatural &&
        previewMatchesGridPixelSize(
            rasterNatural.w,
            rasterNatural.h,
            layoutNatural.w,
            layoutNatural.h,
        )
    ) {
        return { w: layoutNatural.w, h: layoutNatural.h };
    }
    return { w: rasterNatural.w, h: rasterNatural.h };
}

/**
 * True when the editor letterboxes the storage preview inside the live grid canvas.
 * Nav2 map_io maps PNG pixel (0,0) to YAML origin — letterboxed exports shift keep-outs.
 */
export function previewNeedsNav2ResampleExport(
    previewW: number,
    previewH: number,
    gridW: number,
    gridH: number,
): boolean {
    if (previewMatchesGridPixelSize(previewW, previewH, gridW, gridH)) {
        return false;
    }
    const contain = layoutImageContainInGrid(previewW, previewH, gridW, gridH);
    const scaledW = previewW * contain.scale;
    const scaledH = previewH * contain.scale;
    return (
        Math.abs(scaledW - gridW) > 0.5 ||
        Math.abs(scaledH - gridH) > 0.5 ||
        contain.left > 0.5 ||
        contain.top > 0.5
    );
}

/**
 * Maps Fabric stroke coords (editor `contain` layout) → full-grid export (preview
 * stretched to gridW×gridH so each PNG pixel is one occupancy cell).
 */
export function computeCanvasContainToFillGridTransform(
    previewW: number,
    previewH: number,
    gridW: number,
    gridH: number,
): { kx: number; ky: number; tx: number; ty: number } {
    const contain = layoutImageContainInGrid(previewW, previewH, gridW, gridH);
    const kx = gridW / previewW / contain.scale;
    const ky = gridH / previewH / contain.scale;
    return {
        kx,
        ky,
        tx: -contain.left * kx,
        ty: -contain.top * ky,
    };
}

/** Uniform scale + offset to fit a source image in a grid without stretching aspect ratio. */
export function layoutImageContainInGrid(
    srcW: number,
    srcH: number,
    gridW: number,
    gridH: number,
): { scale: number; left: number; top: number } {
    if (srcW <= 0 || srcH <= 0 || gridW <= 0 || gridH <= 0) {
        return { scale: 1, left: 0, top: 0 };
    }
    const scale = Math.min(gridW / srcW, gridH / srcH);
    const scaledW = srcW * scale;
    const scaledH = srcH * scale;
    return {
        scale,
        left: (gridW - scaledW) / 2,
        top: (gridH - scaledH) / 2,
    };
}

/** True when the storage row is a raster variant (edited .png / .bmp), not a .stcm. */
export function isStorageVariantRasterPath(relPath: string): boolean {
    return /\.(bmp|png)$/i.test(relPath.trim());
}

/**
 * Pixel dimensions for letterbox layout and Nav2 export transform.
 * Parent STCM companion BMP matches the original map; variant PNG may differ in size/aspect.
 */
export function pickMapEditorLayoutNatural(
    variantNatural: NaturalImageDims,
    companionNatural: NaturalImageDims,
): NaturalImageDims {
    if (companionNatural && companionNatural.w > 0 && companionNatural.h > 0) {
        return companionNatural;
    }
    return variantNatural;
}

export type MapEditorGeometrySource = {
    layoutNatural: NaturalImageDims;
    /** Background / export geometry image (companion BMP when editing a variant). */
    geometryPreviewUrl: string;
    /** Raster being edited (variant PNG), if different from geometry preview. */
    variantPreviewUrl: string | null;
    usesCompanionGeometry: boolean;
};

/**
 * When editing a variant PNG, align to the parent .stcm companion BMP — not the variant file’s
 * pixel dimensions (often re-encoded with wrong aspect ratio).
 */
export function pickMapEditorGeometrySource(
    variantPreviewUrl: string,
    variantNatural: NaturalImageDims,
    companionPreviewUrl: string | null,
    companionNatural: NaturalImageDims,
): MapEditorGeometrySource | null {
    const hasCompanion =
        Boolean(companionPreviewUrl) &&
        companionPreviewUrl !== variantPreviewUrl &&
        companionNatural &&
        companionNatural.w > 0 &&
        companionNatural.h > 0;
    if (hasCompanion && companionPreviewUrl) {
        return {
            layoutNatural: companionNatural,
            geometryPreviewUrl: companionPreviewUrl,
            variantPreviewUrl: variantPreviewUrl,
            usesCompanionGeometry: true,
        };
    }
    if (variantNatural && variantNatural.w > 0 && variantNatural.h > 0) {
        return {
            layoutNatural: variantNatural,
            geometryPreviewUrl: variantPreviewUrl,
            variantPreviewUrl: null,
            usesCompanionGeometry: false,
        };
    }
    return null;
}

/** True when variant pixels differ in aspect ratio from the companion (layout) image. */
export function variantAspectDiffersFromLayout(
    variantNatural: NaturalImageDims,
    layoutNatural: NaturalImageDims,
    tolerance = 0.02,
): boolean {
    if (!variantNatural || !layoutNatural) {
        return false;
    }
    const va = variantNatural.w / variantNatural.h;
    const la = layoutNatural.w / layoutNatural.h;
    if (!Number.isFinite(va) || !Number.isFinite(la) || la === 0) {
        return false;
    }
    return Math.abs(va - la) / la > tolerance;
}

/** Load natural pixel size of a preview URL (for grid resolution). */
export function loadImageNaturalDimensions(url: string): Promise<NaturalImageDims> {
    return new Promise((resolve) => {
        const im = new window.Image();
        im.onload = () => {
            const w = im.naturalWidth;
            const h = im.naturalHeight;
            resolve(w > 0 && h > 0 ? { w, h } : null);
        };
        im.onerror = () => resolve(null);
        im.src = url;
    });
}
